<template>
    <div dusk="AddOnList" class="flex flex-1 flex-col gap-3">
        <p
            v-if="!visibleAddOns.length"
            dusk="AddOnsEmpty"
            role="status"
            class="px-3 py-6 text-center text-[12.5px] text-theme-toolbar-icon"
        >
            {{ strings.empty }}
        </p>
        <p
            v-else-if="!listedAddOns.length"
            dusk="AddOnsSearchEmpty"
            role="status"
            class="px-3 py-6 text-center text-[12.5px] text-theme-toolbar-icon"
        >
            {{ localizations[lang].searchResultEmpty }}
        </p>
        <div v-for="group in groups" :key="group.key" :dusk="`AddOnGroup-${group.key}`">
            <p
                v-if="group.page !== null"
                dusk="AddOnGroupTitle"
                class="mb-1.5 px-3 text-[10.5px] font-semibold uppercase tracking-wider"
                :class="group.current ? 'text-theme-accent' : 'text-theme-toolbar-icon'"
            >
                {{ group.label }}
                <span class="font-medium">· {{ group.addOns.length }}</span>
                <span v-if="group.current" dusk="AddOnGroupHere" class="font-medium normal-case tracking-normal">
                    · {{ strings.currentPage }}
                </span>
            </p>
            <ul class="m-0 flex list-none flex-col gap-0.5 p-0">
                <li v-for="addOn in group.addOns" :key="addOn.id" class="flex items-center gap-1">
                    <button
                        type="button"
                        :dusk="`AddOnListItem-${addOn.id}`"
                        class="group flex min-h-11 min-w-0 flex-1 cursor-pointer items-start gap-2.5 rounded-lg border-none bg-transparent px-3 py-2.5 text-left transition-colors hover:bg-theme-accent-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent md:min-h-0"
                        @click="open(addOn)"
                        @mouseenter="setAddOnHighlight(addOn.id)"
                        @mouseleave="setAddOnHighlight(null)"
                        @focus="setAddOnHighlight(addOn.id)"
                        @blur="setAddOnHighlight(null)"
                    >
                        <svg-icon
                            class="mt-px shrink-0 text-theme-accent"
                            :icon="addOnIcon(addOn.type)"
                            width="20px"
                            height="20px"
                        />
                        <!-- The row's hover tint takes the byline and the draft pill under 4.5:1
                             in some themes, so both take the text colour while it shows. -->
                        <span class="min-w-0 flex-1">
                            <span class="flex items-center gap-1.5">
                                <span class="min-w-0 truncate text-[13px] font-medium text-theme-accent">{{
                                    addOn.title
                                }}</span>
                                <AddOnDraftPill
                                    v-if="isAddOnDraft(addOn)"
                                    class="shrink-0 group-hover:border-theme-text group-hover:text-theme-text"
                                />
                            </span>
                            <span
                                v-if="addOn.author"
                                dusk="AddOnListItemAuthor"
                                class="mt-0.5 block truncate text-[11px] text-theme-toolbar-icon group-hover:text-theme-text"
                            >
                                {{ localizations[lang].by }} {{ addOn.author }}
                            </span>
                        </span>
                    </button>
                    <PillIconButton
                        v-if="canEditAddOns"
                        v-tooltip.top="strings.edit"
                        :dusk="`AddOnListItemEdit-${addOn.id}`"
                        icon="baseline-edit-24px"
                        :aria-label="`${strings.edit} · ${addOn.title}`"
                        variant="muted"
                        touch-target
                        class="mr-1 shrink-0"
                        @click="edit(addOn)"
                    />
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import AddOnDraftPill from './AddOnDraftPill.vue';
import PillIconButton from '../Components/PillIconButton.vue';
import { addOnIcon } from './addOnIcon.js';
import { addOnStrings } from './addOnStrings.js';
import { isAddOnDraft } from './addOnModel';

export default {
    name: 'AddOnList',
    components: { AddOnDraftPill, PillIconButton },
    computed: {
        ...mapState(['localizations', 'lang']),
        ...mapGetters(['listedAddOns', 'currentPages', 'visibleAddOns', 'canEditAddOns']),
        strings() {
            return addOnStrings(this.localizations, this.lang);
        },
        /**
         * The store already sorts by page (unplaced pieces last) and applies
         * the search, so a group is a run of equal pages. A piece without a
         * page still renders — under no heading — rather than disappear from
         * the list.
         */
        groups() {
            const runs = [];
            for (const addOn of this.listedAddOns) {
                const page = addOn.anchor?.page ?? null;
                const last = runs[runs.length - 1];
                if (last && last.page === page) last.addOns.push(addOn);
                else runs.push({ page, addOns: [addOn] });
            }

            const { page: pageWord } = this.localizations[this.lang];
            return runs.map(run => ({
                ...run,
                key: run.page === null ? 'unplaced' : String(run.page),
                label: run.page === null ? '' : `${pageWord} ${run.page}`,
                current: run.page !== null && this.currentPages.includes(run.page),
            }));
        },
    },
    // A row that unmounts never fires mouseleave, and a marker left open
    // would point at a piece nobody is pointing at.
    beforeDestroy() {
        this.setAddOnHighlight(null);
    },
    methods: {
        ...mapMutations(['setAddOnHighlight']),
        ...mapActions(['goToAddOn', 'editAddOn']),
        addOnIcon,
        isAddOnDraft,
        open(addOn) {
            this.goToAddOn(addOn.id);
        },
        // The form asks before a page turn takes its anchor along, which only
        // holds with the reader on the piece's page, so the piece opens first,
        // the way its row does, and that turns to it.
        edit(addOn) {
            this.goToAddOn(addOn.id);
            this.editAddOn(addOn.id);
        },
        focusFirstRow() {
            this.$el.querySelector('li button')?.focus();
        },
    },
};
</script>
